import React from 'react';
import {
  LineChart,
  Line,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import Card from '../UI/Card';
import { TrendPoint } from '../../api/services/dashboard.service';

interface UserTrendProps {
  trendData: TrendPoint[];
}

const UserTrend: React.FC<UserTrendProps> = ({ trendData }) => {
  const chartData = trendData.map((item) => ({
    ...item,
    formattedCount: `${item.count} users`,
  }));

  const totalUsers = trendData.reduce((sum, d) => sum + d.count, 0);
  const averageUsers = trendData.length ? Math.round(totalUsers / trendData.length) : 0;
  const maxCount = trendData.length ? Math.max(...trendData.map((d) => d.count)) : 0;
  const minCount = trendData.length ? Math.min(...trendData.map((d) => d.count)) : 0;

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-4 rounded-lg shadow-lg border border-gray-200">
          <p className="text-sm font-medium text-gray-600">{payload[0].payload.day}</p>
          <p className="text-lg font-bold text-gray-900">{payload[0].value} users</p>
          <p className="text-xs text-gray-400 mt-1">{payload[0].payload.fullDate}</p>
        </div>
      );
    }
    return null;
  };

  const CustomDot = (props: any) => {
    const { cx, cy, payload } = props;
    const isMax = payload.count === maxCount;
    const isMin = payload.count === minCount && trendData.length > 1;

    return (
      <g>
        <circle
          cx={cx}
          cy={cy}
          r={isMax ? 6 : isMin ? 6 : 4}
          fill={isMax ? '#10B981' : isMin ? '#EF4444' : '#b8860b'}
          stroke="white"
          strokeWidth={2}
        />
        {isMax && (
          <text x={cx} y={cy - 12} textAnchor="middle" fontSize="8" fill="#10B981" fontWeight="bold">
            PEAK
          </text>
        )}
        {isMin && trendData.length > 2 && (
          <text x={cx} y={cy - 12} textAnchor="middle" fontSize="8" fill="#EF4444" fontWeight="bold">
            LOW
          </text>
        )}
      </g>
    );
  };

  return (
    <Card>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">User Trend</h2>
          <p className="text-sm text-gray-500 mt-0.5">Last 30 days new users</p>
        </div>
        <span className="text-xs text-gray-400 bg-gray-100 px-3 py-1 rounded-full">30 Days</span>
      </div>

      <div className="h-72 w-full">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
            <defs>
              <linearGradient id="colorUsers" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#b8860b" stopOpacity={0.2} />
                <stop offset="95%" stopColor="#b8860b" stopOpacity={0} />
              </linearGradient>
            </defs>

            <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" vertical={false} strokeOpacity={0.6} />

            <XAxis
              dataKey="day"
              stroke="#9CA3AF"
              fontSize={11}
              tickLine={false}
              axisLine={false}
              tickMargin={8}
              interval={4}
            />

            <YAxis
              stroke="#9CA3AF"
              fontSize={11}
              tickLine={false}
              axisLine={false}
              tickMargin={8}
              width={40}
            />

            <Tooltip content={<CustomTooltip />} />

            <Area type="monotone" dataKey="count" stroke="none" fill="url(#colorUsers)" fillOpacity={1} />

            <Line
              type="monotone"
              dataKey="count"
              stroke="#b8860b"
              strokeWidth={3}
              dot={<CustomDot />}
              activeDot={{ r: 8, stroke: '#b8860b', strokeWidth: 2, fill: 'white' }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      <div className="mt-4 pt-3 border-t border-gray-100 flex items-center justify-between">
        <div className="flex items-center gap-6">
          <div>
            <p className="text-xs text-gray-500">Total Users</p>
            <p className="text-sm font-semibold text-gray-900">{totalUsers.toLocaleString()}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500">Daily Avg</p>
            <p className="text-sm font-semibold text-gray-900">{averageUsers}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500">Peak Day</p>
            <p className="text-sm font-semibold text-gray-900">{maxCount}</p>
          </div>
        </div>
      </div>
    </Card>
  );
};

export default UserTrend;